import { DateTime } from "luxon";
import {
  DateRangeData,
  LocationData,
  PackageQueryData,
  HotelQueryData,
  HotelSpaQueryData,
} from "../../types/form-data";
import { GuestsData, AcvTokenClaims } from "vacv-components";
const packageData: Partial<PackageQueryData> = {};
const hotelData: Partial<HotelQueryData> = {};
const hotelSpaData: Partial<HotelSpaQueryData> = {};

const setChildAges = (
  guests: GuestsData,
  data:
    | Partial<PackageQueryData>
    | Partial<HotelQueryData>
    | Partial<HotelSpaQueryData>
) => {
  let childrenIndex = 0;
  guests.childAges.forEach((child) => {
    data[`non_adult${childrenIndex + 1}`] = parseInt(child.age);
    childrenIndex++;
  });
  for (let i = 0; i < guests.totalInfants; i++) {
    data[`non_adult${childrenIndex + 1}`] = 1;
    childrenIndex++;
  }
};

const setRoomDetails = (
  guests: GuestsData,
  data:
    | Partial<PackageQueryData>
    | Partial<HotelQueryData>
    | Partial<HotelSpaQueryData>
) => {
  const adultsPerRoom = guests.totalAdults / guests.totalRooms;
  for (let i = 0; i < guests.totalRooms; i++) {
    const roomDetails = [`${adultsPerRoom}-adult-0`];

    if (
      guests.totalChildren > 0 &&
      guests.childAges &&
      guests.childAges.length > 0
    ) {
      guests.childAges.forEach((child) => {
        roomDetails.push("1-child-" + child.age);
      });
    }

    // infants are always last and set to age of 1
    if (guests.totalInfants > 0) {
      for (let infant = 0; infant < guests.totalInfants; infant++) {
        roomDetails.push("1-child-1");
      }
    }

    data[`r${i}`] = roomDetails.join(",");
  }
};

function convertDate(dateStr: string | undefined) {
  if (!dateStr) {
    return "";
  }
  const dt = DateTime.fromFormat(dateStr, "dd/MM/yyyy");
  return dt.toFormat("yyyyMMdd");
}

export const getAlias = (userInfo: AcvTokenClaims | void): string => {
  return userInfo && (userInfo as AcvTokenClaims).alias
    ? ((userInfo as AcvTokenClaims).alias as string)
    : "ACV";
};

export const setPackageQueryData = (
  departureLocation: LocationData,
  arrivalLocation: LocationData,
  dateRange: DateRangeData,
  guests: GuestsData,
  cabinClass: string,
  language: string
) => {
  packageData.departureLocation = departureLocation.code;
  packageData.departureLocationPlaceId = departureLocation.code;
  packageData.departureLocationName = departureLocation.name;
  packageData.departureLocationSuggestionType = departureLocation.type;
  packageData.arrivalLocationPlaceId = arrivalLocation.code;
  packageData.arrivalLocationName = arrivalLocation.name;
  packageData.no_hotel = arrivalLocation.ttsHotelCode;
  packageData.dest_dep = arrivalLocation.ttsDestinationCode;

  packageData.departingDateTime = dateRange.departureDate;
  packageData.returnDateTime = dateRange.returnDate;

  packageData.numberOfRooms = guests.totalRooms;
  packageData.nb_adult = guests.totalAdults;
  packageData.nb_child = guests.totalChildren + guests.totalInfants;

  setChildAges(guests, packageData);
  setRoomDetails(guests, packageData);

  packageData.cabinClass = cabinClass;
  packageData.language = language;

  const cubaDestination = !!arrivalLocation.cubaDestination;
  packageData.cubaDestination = cubaDestination;
  packageData.tripType = "RETURN";

  return packageData;
};

export const setHotelQueryData = (
  arrivalLocation: LocationData,
  dateRange: DateRangeData,
  guests: GuestsData,
  language: string,
  userJWTInfo: AcvTokenClaims | void,
  isMobile: boolean
) => {
  hotelData.dest_dep = arrivalLocation.ttsDestinationCode;
  hotelData.no_hotel = arrivalLocation.ttsHotelCode;

  hotelData.date_dep = convertDate(dateRange.departureDate);
  hotelData.date_ret = convertDate(dateRange.returnDate);

  hotelData.nb_rooms = guests.totalRooms;
  hotelData.nb_adult = guests.totalAdults;
  hotelData.nb_child = guests.totalChildren + guests.totalInfants;

  setChildAges(guests, hotelData);

  hotelData.searchtype = "HO";
  hotelData.alias = getAlias(userJWTInfo);
  hotelData.engines_hotel = "S";
  hotelData.code_ag = "ACV";
  hotelData.flex = "Y";
  hotelData.language = language;
  hotelData.isMobile = isMobile;

  return hotelData;
};

export const setHotelSpaQueryData = (
  arrivalLocation: LocationData,
  dateRange: DateRangeData,
  guests: GuestsData,
  language: string
) => {
  hotelSpaData.arrivalLocationSuggestionType = arrivalLocation.type;
  hotelSpaData.arrivalLocationName = arrivalLocation.name;
  hotelSpaData.arrivalLocation = arrivalLocation.destinationId;
  hotelSpaData.arrivalLocationPlaceId = arrivalLocation.code;
  hotelSpaData.no_hotel = arrivalLocation.ttsHotelCode;
  hotelSpaData.dest_dep = arrivalLocation.ttsDestinationCode;
  hotelSpaData.checkInDate = dateRange.departureDate;
  hotelSpaData.checkOutDate = dateRange.returnDate;
  hotelSpaData.numberOfRooms = guests.totalRooms;
  hotelSpaData.nb_adult = guests.totalAdults;
  hotelSpaData.nb_child = guests.totalChildren + guests.totalInfants;
  setChildAges(guests, hotelSpaData);
  setRoomDetails(guests, hotelSpaData);
  hotelSpaData.language = language;
  hotelSpaData.cubaDestination = arrivalLocation.cubaDestination;

  return hotelSpaData;
};
